import { useState } from "react";
import { Pencil, RefreshCw, RotateCcw, X } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  SkeletonText,
  StatusPill,
  Tabs,
  Textarea,
} from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  useCurrentDailyReflection,
  useCurrentDailyVerse,
  useRegenerateDailyReflection,
  useRestoreAutomaticDailyReflection,
  useUpdateDailyReflection,
} from "@/lib/queries";
import { dailyContentApi } from "@/lib/api";
import { DAILY_CONTENT_STATUS_META } from "@/lib/utils/statusMeta";
import { formatDate, formatDateTime } from "@/lib/utils/format";
import type { DailyReflection, DailyVerse } from "@/lib/api";

const TABS_ID = "daily-content-tabs";

export function DailyContentPage() {
  const [tab, setTab] = useState<"verse" | "reflection">("verse");
  const verseQuery = useCurrentDailyVerse();
  const reflectionQuery = useCurrentDailyReflection();

  return (
    <div>
      <PageHeader
        title="Contenido diario"
        description="Estado actual del versiculo y la reflexion que ve la comunidad hoy."
      />

      <Tabs
        id={TABS_ID}
        label="Contenido diario"
        value={tab}
        onChange={(v) => setTab(v as "verse" | "reflection")}
        items={[
          { value: "verse", label: "Versiculo" },
          { value: "reflection", label: "Reflexion" },
        ]}
      />

      <div className="mt-4">
        {tab === "verse" && (
          <div role="tabpanel" id={`${TABS_ID}-panel-verse`} aria-labelledby={`${TABS_ID}-tab-verse`}>
            <VerseCard query={verseQuery} />
          </div>
        )}
        {tab === "reflection" && (
          <div role="tabpanel" id={`${TABS_ID}-panel-reflection`} aria-labelledby={`${TABS_ID}-tab-reflection`}>
            <ReflectionCard query={reflectionQuery} />
          </div>
        )}
      </div>
    </div>
  );
}

function VerseCard({ query }: { query: ReturnType<typeof useCurrentDailyVerse> }) {
  if (query.isLoading) {
    return (
      <Card>
        <CardBody>
          <SkeletonText lines={5} />
        </CardBody>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <CardBody>
          <ErrorState description="No se pudo cargar el versiculo diario." onRetry={() => query.refetch()} />
        </CardBody>
      </Card>
    );
  }
  const verse = query.data;
  if (!verse) {
    return (
      <Card>
        <CardBody>
          <EmptyState title="Sin versiculo publicado" description="Todavia no hay un versiculo diario para hoy." />
        </CardBody>
      </Card>
    );
  }

  return <VerseDetail verse={verse} />;
}

function VerseDetail({ verse }: { verse: DailyVerse }) {
  return (
    <Card>
      <CardHeader
        title={verse.reference ?? "Sin referencia"}
        description={formatDate(verse.date)}
        action={
          <StatusPill tone={DAILY_CONTENT_STATUS_META[verse.status].tone}>
            {DAILY_CONTENT_STATUS_META[verse.status].label}
          </StatusPill>
        }
      />
      <CardBody className="space-y-4">
        {verse.text && (
          <blockquote className="rounded-lg bg-surface-subtle px-4 py-3 text-sm italic text-ink">
            &ldquo;{verse.text}&rdquo;
          </blockquote>
        )}

        {verse.imageUrl && (
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">Imagen</p>
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <img src={verse.imageUrl} alt="" className="max-h-64 rounded-lg border border-ink/10 object-cover" />
          </div>
        )}

        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-faint">Creado</dt>
            <dd className="text-ink">{formatDateTime(verse.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-faint">Actualizado</dt>
            <dd className="text-ink">{formatDateTime(verse.updatedAt)}</dd>
          </div>
        </dl>

        {verse.generation && Object.keys(verse.generation).length > 0 && (
          <MetadataBlock title="Metadatos de generacion" data={verse.generation} />
        )}
        {verse.imageMetadata && Object.keys(verse.imageMetadata).length > 0 && (
          <MetadataBlock title="Metadatos de imagen" data={verse.imageMetadata} />
        )}

        <div className="rounded-lg border border-ink/10 bg-surface-subtle px-3 py-2.5 text-sm text-ink-faint">
          El versiculo se actualiza desde Olivia. No hay una accion manual disponible aqui.
        </div>
      </CardBody>
    </Card>
  );
}

function ReflectionCard({ query }: { query: ReturnType<typeof useCurrentDailyReflection> }) {
  if (query.isLoading) {
    return (
      <Card>
        <CardBody>
          <SkeletonText lines={5} />
        </CardBody>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <CardBody>
          <ErrorState description="No se pudo cargar la reflexion diaria." onRetry={() => query.refetch()} />
        </CardBody>
      </Card>
    );
  }
  const reflection = query.data;
  if (!reflection) {
    return (
      <Card>
        <CardBody>
          <EmptyState title="Sin reflexion publicada" description="Todavia no hay una reflexion diaria para hoy." />
        </CardBody>
      </Card>
    );
  }

  return <ReflectionDetail reflection={reflection} />;
}

function ReflectionDetail({ reflection }: { reflection: DailyReflection }) {
  return (
    <Card>
      <CardHeader
        title={reflection.title ?? "Sin titulo"}
        description={formatDate(reflection.date)}
        action={
          <StatusPill tone={DAILY_CONTENT_STATUS_META[reflection.status].tone}>
            {DAILY_CONTENT_STATUS_META[reflection.status].label}
          </StatusPill>
        }
      />
      <CardBody className="space-y-4">
        {reflection.body && <p className="whitespace-pre-line text-sm text-ink">{reflection.body}</p>}

        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-faint">Coste estimado</dt>
            <dd className="text-ink">
              {reflection.estimatedCost !== null ? `$${reflection.estimatedCost.toFixed(4)}` : "-"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-faint">Creado</dt>
            <dd className="text-ink">{formatDateTime(reflection.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-faint">Actualizado</dt>
            <dd className="text-ink">{formatDateTime(reflection.updatedAt)}</dd>
          </div>
        </dl>

        {reflection.generation && Object.keys(reflection.generation).length > 0 && (
          <MetadataBlock title="Metadatos de generacion" data={reflection.generation} />
        )}

        <ReflectionActions reflection={reflection} />
      </CardBody>
    </Card>
  );
}

function ReflectionActions({ reflection }: { reflection: DailyReflection }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState(reflection.body ?? "");
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);

  const updateReflection = useUpdateDailyReflection();
  const regenerateReflection = useRegenerateDailyReflection();
  const restoreReflection = useRestoreAutomaticDailyReflection();

  const dirty = draftBody !== (reflection.body ?? "");

  function startEditing() {
    setDraftBody(reflection.body ?? "");
    setEditing(true);
  }

  function cancelEditing() {
    setDraftBody(reflection.body ?? "");
    setEditing(false);
  }

  async function handleSave() {
    if (!dailyContentApi.REFLECTION_CAPABILITIES.edit) return;
    try {
      await updateReflection.mutateAsync(draftBody);
      toast({ title: "Reflexion actualizada", variant: "success" });
      setEditing(false);
    } catch {
      toast({
        title: "No se pudo guardar",
        description: "El backend rechazo el cambio. La reflexion no se modifico.",
        variant: "error",
      });
    }
  }

  async function handleRegenerate() {
    try {
      await regenerateReflection.mutateAsync();
      toast({ title: "Reflexion regenerada", variant: "success" });
      setRegenerateOpen(false);
    } catch {
      toast({ title: "No se pudo regenerar la reflexion", variant: "error" });
    }
  }

  async function handleRestore() {
    if (!dailyContentApi.REFLECTION_CAPABILITIES.restoreAutomatic) return;
    try {
      await restoreReflection.mutateAsync();
      toast({ title: "Reflexion restaurada a la version automatica", variant: "success" });
    } catch {
      toast({ title: "No se pudo restaurar la reflexion automatica", variant: "error" });
    }
  }

  return (
    <div className="space-y-3 border-t border-ink/10 pt-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-faint">Acciones</p>

      <div className="flex flex-wrap gap-2">
        <ActionButton
          icon={<Pencil className="h-4 w-4" aria-hidden="true" />}
          label="Editar"
          supported={dailyContentApi.REFLECTION_CAPABILITIES.edit}
          onClick={startEditing}
          disabled={editing}
        />
        <ActionButton
          icon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
          label="Regenerar"
          supported={dailyContentApi.REFLECTION_CAPABILITIES.regenerate}
          onClick={() => setRegenerateOpen(true)}
        />
        <ActionButton
          icon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
          label="Restaurar automatica"
          supported={dailyContentApi.REFLECTION_CAPABILITIES.restoreAutomatic}
          onClick={() => setRestoreOpen(true)}
        />
      </div>

      {(!dailyContentApi.REFLECTION_CAPABILITIES.edit ||
        !dailyContentApi.REFLECTION_CAPABILITIES.restoreAutomatic) && (
          <div className="flex items-start gap-2 rounded-lg border border-dashed border-ink/15 bg-surface-subtle px-3 py-2.5 text-sm text-ink-faint">
            <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Olivia todavia no permite editar manualmente ni restaurar automatico. Por eso esos
              controles permanecen deshabilitados. Regenerar si esta disponible y se ejecuta contra
              Olivia.
            </span>
          </div>
        )}

      {editing && (
        <div className="space-y-2 rounded-lg border border-ink/10 bg-surface-subtle p-3">
          <Textarea
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            rows={8}
            disabled={updateReflection.isPending}
            aria-label="Contenido de la reflexion"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-ink-faint">{dirty ? "Cambios sin guardar." : "Sin cambios."}</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<X className="h-3.5 w-3.5" aria-hidden="true" />}
                onClick={cancelEditing}
                disabled={updateReflection.isPending}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={() => void handleSave()}
                loading={updateReflection.isPending}
                disabled={!dirty || !dailyContentApi.REFLECTION_CAPABILITIES.edit}
                title={
                  dailyContentApi.REFLECTION_CAPABILITIES.edit
                    ? undefined
                    : "Olivia todavia no soporta override manual"
                }
              >
                Guardar
              </Button>
            </div>
          </div>
          {!dailyContentApi.REFLECTION_CAPABILITIES.edit && (
            <p className="text-xs text-warning-700">
              El backend todavia no acepta cambios aqui - puedes redactar el texto, pero &quot;Guardar&quot;
              permanecera deshabilitado hasta que Olivia soporte override manual.
            </p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        onConfirm={handleRegenerate}
        title="Regenerar reflexion"
        description="Se solicitara una nueva version generada automaticamente para la fecha actual. El contenido actual se sustituira cuando el backend confirme el resultado."
        confirmLabel="Regenerar"
      />

      <ConfirmDialog
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onConfirm={handleRestore}
        title="Restaurar version automatica"
        description="Esto eliminara cualquier modificacion manual y volvera a la ultima version generada automaticamente para esta reflexion."
        confirmLabel="Restaurar"
        variant="danger"
      />
    </div>
  );
}

function ActionButton({
  icon,
  label,
  supported,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  supported: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      icon={icon}
      onClick={onClick}
      disabled={!supported || disabled}
      title={supported ? undefined : "Bloqueado hasta que Olivia soporte esta funcion"}
    >
      {label}
      {!supported && <span className="ml-1 text-ink-faint">(Olivia pendiente)</span>}
    </Button>
  );
}

function MetadataBlock({ title, data }: { title: string; data: Record<string, unknown> }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">{title}</p>
      <pre className="scrollbar-thin overflow-x-auto rounded-lg bg-surface-subtle p-3 text-xs text-ink-muted">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
